import { importLibrary, setOptions } from "@googlemaps/js-api-loader";
import { EshopUrl } from "../../../shared/js/config";
import { assert } from "../../../js/common/assert";
import elementService from "../../../js/common/ElementService";

const daysOfWeek = ["Neděle", "Pondělí", "Úterý", "Středa", "Čtvrtek", "Pátek", "Sobota"];
const onDaysOfWeek = ["v neděli", "v pondělí", "v úterý", "ve středu", "ve čtvrtek", "v pátek", "v sobotu"];

interface MapInfo {
    lat: number;
    lng: number;
    title: string;
    url: string;
}

interface OpeningSpan {
    from: number;
    to: number;
}

interface FullOpeningStatus {
    type: string;
    status: string;
    info: string;

}

interface Break {
    title: string;
    startsAt: string;
    endsAt: string;
}

interface DayOpening {
    isOpen: boolean;
    opensAt?: string;
    closesAt?: string;
    break?: Break;
}

interface RegularDayOpening extends DayOpening {
    dayOfWeek: number;
}

interface SpecificDayOpening extends DayOpening {
    date: string;
    note?: string;
}

interface OpeningHours {
    regularDays: RegularDayOpening[];
    holidays: {
        isOpen?: boolean;
        note?: string;
    };
    specificDays?: SpecificDayOpening[];
}

interface Store {
    city: string;
    addressStreet: string;
    addressZip: string;
    addressCity: string;
    detailUrl: string;
    map: MapInfo;
    openingHours: OpeningHours;
}

export default class StoresPageService {
    private stores: Store[] = [];
    private map: google.maps.Map | undefined = undefined;
    private readonly markers: google.maps.marker.AdvancedMarkerElement[] = [];
    private currentInfoWindow: google.maps.InfoWindow | undefined = undefined;
    private readonly googleMapsApiKey = "AIzaSyDf0EmlaW6NWsTkbEKBqQpEM-THziCNH-4";
    private readonly mapCenter = { lat: 49.9095, lng: 15.0149 };

    public async init(): Promise<void> {
        this.stores = [
            {
                city: "Hradec Králové",
                addressStreet: "Bieblova 1202",
                addressZip: "500 03",
                addressCity: "Hradec Králové",
                detailUrl: "prodejna-hradec-bieblova",
                map: {
                    lat: 50.219_733_622_416_69,
                    lng: 15.865_744_395_950_82,
                    title: "Hradec Králové - Bieblova",
                    url: "https://maps.app.goo.gl/LZiwLckVCMpxkJUT6",
                },
                openingHours: {
                    regularDays: [
                        {
                            dayOfWeek: 1,
                            isOpen: true,
                            opensAt: "10:00",
                            closesAt: "18:00",
                        },
                        {
                            dayOfWeek: 2,
                            isOpen: true,
                            opensAt: "10:00",
                            closesAt: "18:00",
                        },
                        {
                            dayOfWeek: 3,
                            isOpen: true,
                            opensAt: "10:00",
                            closesAt: "18:00",
                        },
                        {
                            dayOfWeek: 4,
                            isOpen: true,
                            opensAt: "10:00",
                            closesAt: "18:00",
                        },
                        {
                            dayOfWeek: 5,
                            isOpen: true,
                            opensAt: "10:00",
                            closesAt: "18:00",
                        },
                        {
                            dayOfWeek: 6,
                            isOpen: false,
                        },
                        {
                            dayOfWeek: 0,
                            isOpen: false,
                        },
                    ],
                    specificDays: [
                        {
                            date: "2025-04-18",
                            isOpen: false,
                        },
                        {
                            date: "2025-04-19",
                            isOpen: false,
                        },
                    ],
                    holidays: {
                        isOpen: false,
                        note: "Otevírací doba se může změnit.",
                    },
                },
            },
            {
                city: "Plzeň",
                addressStreet: "Otýlie Beníškové 470/6",
                addressZip: "301 00",
                addressCity: "Plzeň 1",
                detailUrl: "prodejna-plzen-otylie-beniskove",
                map: {
                    lat: 49.752_494_103_811_685,
                    lng: 13.378_902_541_228_172,
                    title: "Plzeň - Otýlie Beníškové",
                    url: "https://maps.app.goo.gl/EUY1hVS8AtLdAdd97",
                },
                openingHours: {
                    regularDays: [
                        {
                            dayOfWeek: 1,
                            isOpen: true,
                            opensAt: "10:00",
                            closesAt: "19:00",
                        },
                        {
                            dayOfWeek: 2,
                            isOpen: true,
                            opensAt: "10:00",
                            closesAt: "19:00",
                        },
                        {
                            dayOfWeek: 3,
                            isOpen: true,
                            opensAt: "10:00",
                            closesAt: "19:00",
                        },
                        {
                            dayOfWeek: 4,
                            isOpen: true,
                            opensAt: "10:00",
                            closesAt: "19:00",
                        },
                        {
                            dayOfWeek: 5,
                            isOpen: true,
                            opensAt: "10:00",
                            closesAt: "19:00",
                        },
                        {
                            dayOfWeek: 6,
                            isOpen: false,
                        },
                        {
                            dayOfWeek: 0,
                            isOpen: false,
                        },
                    ],
                    specificDays: [
                        {
                            date: "2025-04-18",
                            isOpen: false,
                        },
                        {
                            date: "2025-04-19",
                            isOpen: false,
                        },
                    ],
                    holidays: {
                        isOpen: false,
                        note: "Otevírací doba se může změnit.",
                    },
                },
            },
            {
                city: "Praha",
                addressStreet: "Dukelských Hrdinů 40",
                addressZip: "170 00",
                addressCity: "Praha 7",
                detailUrl: "prodejna-praha-dukelskych-hrdinu",
                map: {
                    lat: 50.102_277_280_517_406,
                    lng: 14.433_062_870_718_592,
                    title: "Praha - Dukelských Hrdinů",
                    url: "https://maps.app.goo.gl/sRrD9gM2P6nGG7hc7",
                },
                openingHours: {
                    regularDays: [
                        {
                            dayOfWeek: 1,
                            isOpen: true,
                            opensAt: "12:00",
                            closesAt: "19:00",
                        },
                        {
                            dayOfWeek: 2,
                            isOpen: true,
                            opensAt: "12:00",
                            closesAt: "19:00",
                        },
                        {
                            dayOfWeek: 3,
                            isOpen: true,
                            opensAt: "12:00",
                            closesAt: "19:00",
                        },
                        {
                            dayOfWeek: 4,
                            isOpen: true,
                            opensAt: "12:00",
                            closesAt: "19:00",
                        },
                        {
                            dayOfWeek: 5,
                            isOpen: true,
                            opensAt: "12:00",
                            closesAt: "19:00",
                        },
                        {
                            dayOfWeek: 6,
                            isOpen: false,
                        },
                        {
                            dayOfWeek: 0,
                            isOpen: false,
                        },
                    ],
                    specificDays: [
                        {
                            date: "2025-04-18",
                            isOpen: false,
                        },
                        {
                            date: "2025-04-19",
                            isOpen: false,
                        },
                    ],
                    holidays: {
                        isOpen: false,
                        note: "Otevírací doba se může změnit.",
                    },
                },
            },
            {
                city: "Tábor",
                addressStreet: "Vančurova 1946/3",
                addressZip: "390 01",
                addressCity: "Tábor",
                detailUrl: "prodejna-tabor-vancurova",
                map: {
                    lat: 49.413_895_556_689_916,
                    lng: 14.666_131_558_125_43,
                    title: "Tábor - Vančurova",
                    url: "https://maps.app.goo.gl/jF6hwwAypDxF5Mdg8",
                },
                openingHours: {
                    regularDays: [
                        {
                            dayOfWeek: 1,
                            isOpen: true,
                            opensAt: "10:00",
                            closesAt: "17:00",
                            break: {
                                title: "polední pauza",
                                startsAt: "12:00",
                                endsAt: "13:00",
                            },
                        },
                        {
                            dayOfWeek: 2,
                            isOpen: true,
                            opensAt: "10:00",
                            closesAt: "17:00",
                            break: {
                                title: "polední pauza",
                                startsAt: "12:00",
                                endsAt: "13:00",
                            },
                        },
                        {
                            dayOfWeek: 3,
                            isOpen: true,
                            opensAt: "10:00",
                            closesAt: "17:00",
                            break: {
                                title: "polední pauza",
                                startsAt: "12:00",
                                endsAt: "13:00",
                            },
                        },
                        {
                            dayOfWeek: 4,
                            isOpen: true,
                            opensAt: "10:00",
                            closesAt: "17:00",
                            break: {
                                title: "polední pauza",
                                startsAt: "12:00",
                                endsAt: "13:00",
                            },
                        },
                        {
                            dayOfWeek: 5,
                            isOpen: true,
                            opensAt: "10:00",
                            closesAt: "17:00",
                            break: {
                                title: "polední pauza",
                                startsAt: "12:00",
                                endsAt: "13:00",
                            },
                        },
                        {
                            dayOfWeek: 6,
                            isOpen: false,
                        },
                        {
                            dayOfWeek: 0,
                            isOpen: false,
                        },
                    ],
                    specificDays: [
                        {
                            date: "2025-04-18",
                            isOpen: false,
                        },
                        {
                            date: "2025-04-19",
                            isOpen: false,
                        },
                    ],
                    holidays: {
                        isOpen: false,
                        note: "Otevírací doba se může změnit.",
                    },
                },
            },
        ];
        this.generateContent();
        await this.initMap();
        this.setupEventListeners();
    }

    public async initMap(): Promise<void> {
        try {
            setOptions({ key: this.googleMapsApiKey, language: "cs", libraries: ["marker"], v: "weekly" });

            await importLibrary("maps");
            await importLibrary("marker");
        } catch {
            return;
        }

        const mapContainer: HTMLElement | null = document.querySelector<HTMLElement>("#map");

        if (mapContainer === null) {
            return;
        }

        const zoomLevel = window.innerWidth < 768 ? 7 : 8;

        this.map = new google.maps.Map(mapContainer, {
            center: this.mapCenter,
            zoom: zoomLevel,
            mapId: "odkarla_cz_prodejny",
            gestureHandling: "greedy",
        });

        this.addMarkers();
    }

    private closeCurrentInfoWindow(): void {
        if (this.currentInfoWindow) {
            this.currentInfoWindow.close();
            this.currentInfoWindow = undefined;
        }
    }

    private addMarkers(): void {
        for (const store of this.stores) {
            const markerContent = document.createElement("div");
            markerContent.classList.add("custom-marker");

            const marker = new google.maps.marker.AdvancedMarkerElement({
                position: { lat: store.map.lat, lng: store.map.lng },
                map: this.map,
                title: store.map.title,
                content: markerContent,
            });

            // Kliknutí na marker
            marker.addListener("click", () => {
                this.closeCurrentInfoWindow();

                const infoWindow = new google.maps.InfoWindow({
                    content: this.getInfoWindowContent(store),
                });

                infoWindow.open({
                    map: this.map,
                    anchor: marker,
                });

                this.currentInfoWindow = infoWindow;

                // Zavření okna při kliknutí na tlačítko zavření
                setTimeout(() => {
                    const closeButton = document.querySelector<HTMLButtonElement>(".store-close-button");

                    if (closeButton) {
                        closeButton.addEventListener("click", () => {
                            this.closeCurrentInfoWindow();
                        });
                    }
                }, 100);
            });

            this.markers.push(marker);
        }
    }

    private getInfoWindowContent(store: Store): string {
        return `
            <div class="store-info-window">
                <div class="store-info-logo"></div>
                <div class="store-info-detail">
                    <h4>${store.map.title}</h4>
                    <p>${store.addressStreet}, ${store.addressZip} ${store.addressCity}</p>
                    <p><a href="${EshopUrl}${store.detailUrl}" target="_self" class="store-link">Detail prodejny</a></p>
                </div>
                <button class="store-close-button">×</button>
            </div>
        `;
    }

    private getTodayInString(): string {
        const today = new Date();
        const year = today.getFullYear();
        const month = (today.getMonth() + 1).toString().padStart(2, "0");
        const day = today
            .getDate()
            .toString()
            .padStart(2, "0");

        return `${year}-${month}-${day}`;
    }

    private getTodayOpening(openingHours: OpeningHours): DayOpening | undefined {
        const today = new Date();
        const dayOfWeek = today.getDay();
        const todayInString = this.getTodayInString();
        const specificDays = openingHours.specificDays;
        const regularDays = openingHours.regularDays;

        /* find if today is in specific days */
        if (specificDays) {
            const specificDay = specificDays.find((day) => day.date === todayInString);

            if (specificDay) {
                return specificDay;
            }
        }

        /* find if today is in regular days */
        const regularDay = regularDays.find((day) => day.dayOfWeek === dayOfWeek);

        if (regularDay) {
            return regularDay;
        }

        return undefined;
    }

    private getOpeningSpans(dayOpening: DayOpening): OpeningSpan[] | undefined {
        const openingSpans: OpeningSpan[] | undefined = [];
        const openingBreak: Break | undefined = dayOpening.break;

        if (dayOpening.isOpen) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const opensAt: number = this.timeToMinutes(dayOpening.opensAt!);
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const closesAt: number = this.timeToMinutes(dayOpening.closesAt!);

            if (openingBreak) {
                const breakFrom: number = this.timeToMinutes(openingBreak.startsAt);
                const breakTo: number = this.timeToMinutes(openingBreak.endsAt);

                openingSpans.push(
                    { from: opensAt, to: breakFrom },
                    { from: breakTo, to: closesAt },
                );

                return openingSpans;
            }

            openingSpans.push(
                { from: opensAt, to: closesAt },
            );
        }

        return openingSpans;
    }

    private timeToMinutes(time: string): number {
        const [hours, minutes] = time.split(":").map(Number);

        return (hours * 60) + minutes;
    }

    private getCurrentTime(): string {
        const now = new Date();

        return now.toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit", hour12: false });
    }

    private isStoreOpen(day: DayOpening): boolean {
        const now: number = this.timeToMinutes(this.getCurrentTime());

        if (!day.isOpen) {
            return false;
        }

        const openingSpans = this.getOpeningSpans(day);

        assert(openingSpans !== undefined);

        for (const span of openingSpans) {
            if (now >= span.from && now <= span.to) {
                return true;
            }
        }

        return false;
    }

    private getDayOfWeek(date: string): number {
        const parsedDate = new Date(date);

        return parsedDate.getDay();
    }

    private getNextDayOpening(store: Store): string {
        const today = new Date();
        const followingDays: string[] = [];
        const specificDays: SpecificDayOpening[] | undefined = store.openingHours.specificDays;
        const regularDays: RegularDayOpening[] = store.openingHours.regularDays;

        for (let index = 1; index <= 14; index++) {
            const nextDay = new Date(today);
            nextDay.setDate(today.getDate() + index);

            const formattedDate = nextDay.toISOString().split("T")[0];
            followingDays.push(formattedDate);
        }

        for (const [index, day] of followingDays.entries()) {
            if (specificDays) {
                const specificDay = specificDays.find((sd) => sd.date === day && sd.isOpen);

                if (specificDay) {
                    const specificDayOfWeek: number = this.getDayOfWeek(specificDay.date);

                    return (index === 0)
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        ? `otevře zítra ${this.getCorrectTimeConjunction(specificDay.opensAt!)} ${this.formatTime(specificDay.opensAt!)}`
                        : `otevře ${onDaysOfWeek[specificDayOfWeek]} od ${this.formatDate(specificDay.date)}`;
                }
            }

            const dayOfWeek: number = this.getDayOfWeek(day);
            const regularDay = regularDays.find((rd) => rd.dayOfWeek === dayOfWeek && rd.isOpen);

            if (regularDay) {
                return (index === 0)
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    ? `otevře zítra od ${this.formatTime(regularDay.opensAt!)}`
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    : `otevře ${onDaysOfWeek[dayOfWeek]} ${this.getCorrectTimeConjunction(regularDay.opensAt!)} ${this.formatTime(regularDay.opensAt!)}`;
            }
        }

        return "Bez dalších informací";
    }

    private formatTime(time: string): string {
        const [hours, minutes] = time.split(":").map(Number);

        if (minutes === 0) {
            return String(hours);
        }

        return time;
    }

    private formatDate(date: string): string {
        const [, month, day] = date.split("-").map(Number);

        return `${day}.${month}.`;
    }

    private getOpeningInfo(today: DayOpening, store: Store): string {
        // const openingSpans = this.getOpeningSpans(today);
        const now: number = this.timeToMinutes(this.getCurrentTime());
        const breakOpening: Break | undefined = today.break;

        if (!today.isOpen) {
            return this.getNextDayOpening(store);
        }

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const opensAt: number = this.timeToMinutes(today.opensAt!);
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const closesAt: number = this.timeToMinutes(today.closesAt!);

        if (now > opensAt && breakOpening) {
            const startsAt: number = this.timeToMinutes(breakOpening.startsAt);
            const endsAt: number = this.timeToMinutes(breakOpening.endsAt);

            if (now >= startsAt && now <= endsAt) {
                return `${breakOpening.title} do ${this.formatTime(breakOpening.endsAt)}`;
            }
        }

        if (now > closesAt) {
            return this.getNextDayOpening(store);
        }

        if ((opensAt - now) < 60) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            return `brzy otevírá - ${this.getCorrectTimeConjunction(today.opensAt!)} ${this.formatTime(today.opensAt!)}`;
        }

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return `otevírá dnes ${this.getCorrectTimeConjunction(today.opensAt!)} ${this.formatTime(today.opensAt!)}`;
    }

    private getCorrectTimeConjunction(time: string): string {
        const hour = Number.parseInt(time.split(":")[0], 10);

        if ([2, 3, 4, 12, 13, 14, 20, 21, 22, 23, 24].includes(hour)) {
            return "ve";
        }

        return "v";
    }

    private getClosingInfo(day: DayOpening): string {
        const openingSpans = this.getOpeningSpans(day);
        const now: number = this.timeToMinutes(this.getCurrentTime());
        const breakOpening: Break | undefined = day.break;

        if (breakOpening !== undefined && openingSpans !== undefined) {
            if (now >= openingSpans[0].from && now <= openingSpans[0].to) {
                if ((openingSpans[0].to - now) < 60) {
                    return `od ${this.formatTime(breakOpening.startsAt)} - ${breakOpening.title}`;
                }
            } else if ((openingSpans[1].to - now) < 60) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                return `brzy zavírá - ${this.getCorrectTimeConjunction(day.closesAt!)} ${this.formatTime(day.closesAt!)}`;
            }
        }

        // eslint-disable-next-line unicorn/prefer-at
        if (openingSpans !== undefined && (openingSpans[openingSpans.length - 1].to - now) < 60) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            return `brzy zavírá - ${this.getCorrectTimeConjunction(day.closesAt!)} ${this.formatTime(day.closesAt!)}`;
        }

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return `dnes do ${this.formatTime(this.formatTime(day.closesAt!))}`;
    }

    private getFullOpeningStatus(store: Store): string {
        const today: DayOpening | undefined = this.getTodayOpening(store.openingHours);

        const fullOpeningStatus = !today
            ? {
                type: "closed",
                status: "Chyba",
                info: "otevírací doba není známa",
            }
            : {
                type: this.isStoreOpen(today) ? "open" : "closed",
                status: this.isStoreOpen(today) ? "Otevřeno" : "Zavřeno",
                info: this.isStoreOpen(today) ? this.getClosingInfo(today) : this.getOpeningInfo(today, store),
            };

        return this.formatOpeningStatus(fullOpeningStatus);
    }

    private formatOpeningStatus(fullOpeningStatus: FullOpeningStatus): string {
        const status = fullOpeningStatus.status;
        const statusClass = fullOpeningStatus.type;
        const statusInfo = fullOpeningStatus.info;

        return `<span class='store-status-${statusClass}'>${status}</span> - ${statusInfo}`;
    }

    private getRegularOpeningHours(openingHours: OpeningHours): string {
        let regularOpeningHours = "<p><b>Běžná otevírací doba</b></p>";
        const regularDays = openingHours.regularDays;

        for (const regularDay of regularDays) {
            let opening = "zavřeno";
            const openingBreak = regularDay.break;

            if (regularDay.isOpen && regularDay.opensAt !== undefined && regularDay.closesAt !== undefined) {
                opening = (openingBreak)
                    ? `<span>${this.formatTime(regularDay.opensAt)} - ${this.formatTime(openingBreak.startsAt)}</span> <span>${this.formatTime(openingBreak.endsAt)} - ${this.formatTime(regularDay.closesAt)}</span>`
                    : `<span>${this.formatTime(regularDay.opensAt)} - ${this.formatTime(regularDay.closesAt)}</span>`;
            }

            regularOpeningHours += `<p><span>${daysOfWeek[regularDay.dayOfWeek]}</span> ${opening}</p>`;
        }

        return regularOpeningHours;
    }

    private getHolidayOpeningHours(openingHours: OpeningHours): string {
        const holidays = openingHours.holidays;
        const holidayOpeningHours = "<br><p class='mt-4'><span><b>Svátky</b></span>";
        const isOpen = holidays.isOpen;

        return (isOpen === true)
            ? `${holidayOpeningHours} <span>otevřeno</span></p>`
            : `${holidayOpeningHours} <span>zavřeno</span></p>`;
    }

    private generateContent(): void {
        const storeElement = document.querySelector<HTMLElement>(".stores-list");

        if (!storeElement) {
            return;
        }

        let storesOutput = "";
        let previousCity = "";

        let index = 0;

        for (const store of this.stores) {
            if (store.city !== previousCity) {
                storesOutput += `<h3>${store.city}</h3>`;
                previousCity = store.city;
            }

            storesOutput += `
                <div class="stores-grid-table">
                    <div class="stores-grid-row">
                        <div class="stores-grid-cell"><i class="fas fa-map-marker-alt"></i></div>
                        <div class="stores-grid-cell">${store.addressStreet}</div>
                    </div>
                    <div class="stores-grid-row">
                        <div class="stores-grid-cell"></div>
                        <div class="stores-grid-cell">${store.addressZip}&nbsp;${store.addressCity}</div>
                    </div>
                    <div class="stores-grid-row">
                        <div class="stores-grid-cell"><i class="far fa-clock"></i></div>
                        <div class="stores-grid-cell">${this.getFullOpeningStatus(store)}<a class="store-show-full-hours" data-key="${index}" data-open="false"><i class="fa fa-chevron-down"></i></a></div>
                    </div>
                    <div class="stores-grid-row hidden" id="hours-${index}">
                        <div class="stores-grid-cell"></div>
                        <div class="stores-grid-cell">
                            <div class="stores-opening-hours">
                                ${this.getRegularOpeningHours(store.openingHours)}
                                ${this.getHolidayOpeningHours(store.openingHours)}
                            </div>
                        </div>
                    </div>
                    <div class="stores-grid-row">
                        <div class="stores-grid-cell"></div>
                        <div class="stores-grid-cell store-links">
                            <a href="${EshopUrl}${store.detailUrl}" target="_self" class="store-link">Detail prodejny</a>&nbsp;&nbsp;<a href="${store.map.url}" class="store-link show-on-map">Ukázat na mapě</a>
                        </div>
                    </div>
                </div>
            `;
            index++;
        }

        storeElement.innerHTML = storesOutput;
    }

    private scrollTo(element: HTMLElement | null): void {
        if (element) {
            element.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }
    }

    private setupEventListeners(): void {
        // Odkaz "Chci vlastni prodejnu"
        const wantMyOwnStore = document.querySelector<HTMLElement>("#wantMyOwnStore");
        const myOwnStore = document.querySelector<HTMLElement>("#vlastni-prodejna");
        const mapElement = document.querySelector<HTMLElement>(".stores-map");

        if (wantMyOwnStore && myOwnStore) {
            wantMyOwnStore.addEventListener("click", (event) => {
                event.preventDefault();
                this.scrollTo(myOwnStore);
            });
        }

        // Klik pro zobrazení otevíracích hodin u prodejny
        const showFullHours = document.querySelectorAll<HTMLElement>(".store-show-full-hours");

        for (const store of showFullHours) {
            store.addEventListener("click", (event) => {
                event.preventDefault();
                const isOpen = elementService.elementData(store, "open");
                const key = elementService.elementData(store, "key");
                const openingHours = document.querySelector<HTMLElement>(`#hours-${key}`);

                if (isOpen === "false") {
                    store.innerHTML = "<i class='fa fa-chevron-up'></i>";

                    if (openingHours) {
                        openingHours.classList.remove("hidden");
                    }
                } else {
                    store.innerHTML = "<i class='fa fa-chevron-down'></i>";

                    if (openingHours) {
                        openingHours.classList.add("hidden");
                    }
                }

                store.dataset.open = isOpen === "false" ? "true" : "false";
            });
        }

        const showOnMapLinks = document.querySelectorAll<HTMLElement>(".show-on-map");

        for (const link of showOnMapLinks) {
            link.addEventListener("click", (event) => {
                event.preventDefault();
                const href = link.getAttribute("href");

                const store = this.stores.find((s) => s.map.url === href);

                if (store && this.map) {
                    const marker: google.maps.marker.AdvancedMarkerElement | undefined
                        = this.markers.find((m: google.maps.marker.AdvancedMarkerElement) => m.position?.lat === store.map.lat && m.position.lng === store.map.lng);

                    if (marker) {
                        this.closeCurrentInfoWindow();
                        this.map.setZoom(15);
                        this.map.setCenter(marker.position as google.maps.LatLng);

                        const infoWindow = new google.maps.InfoWindow({
                            content: this.getInfoWindowContent(store),
                        });

                        infoWindow.open({
                            map: this.map,
                            anchor: marker,
                        });

                        this.currentInfoWindow = infoWindow;

                        // Přidání event listeneru pro zavření info okna
                        setTimeout(() => {
                            const closeButton = document.querySelector<HTMLButtonElement>(".store-close-button");

                            if (closeButton) {
                                closeButton.addEventListener("click", () => {
                                    this.closeCurrentInfoWindow();
                                });
                            }
                        }, 100);
                    }

                    this.scrollTo(mapElement);
                }
            });
        }

        window.addEventListener("resize", () => {
            const newZoom = window.innerWidth < 768 ? 7 : 8;

            if (this.map !== undefined) {
                this.map.setZoom(newZoom);
            }
        });
    }
}
